import { sapReservationsService } from "@/apis/services/sap-reservations/sapReservation.service"
import { IGetEmployeeByIdParams, IPaginationMeta, ISapReservation, ISapReservationDto } from "@/types"
import { create } from "zustand"
import { devtools } from "zustand/middleware"

interface ISapReservationState {
  reservations: ISapReservation[]
  currentReservation: ISapReservation | null
  meta?: IPaginationMeta
  isLoading: boolean
  error: string | null
  fetchSapReservations: (params: IGetEmployeeByIdParams) => Promise<void>
  fetchSapReservationById: (recordId: string) => Promise<void>
  createSapReservation: (dto: ISapReservationDto) => Promise<ISapReservation>
}

const initialState = {
  reservations: [],
  currentReservation: null,
  meta: undefined,
  isLoading: false,
  error: null,
}

export const useSapReservationStore = create<ISapReservationState>()(
  devtools(set => ({
    ...initialState,

    fetchSapReservations: async (params: IGetEmployeeByIdParams) => {
      set({ isLoading: true, error: null })
      try {
        const response = await sapReservationsService.getSapReservations(params)
        set({ reservations: response.data.items, meta: response.data.meta })
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "An unknown error occurred"
        set({ error: errorMessage })
        throw error
      } finally {
        set({ isLoading: false })
      }
    },

    fetchSapReservationById: async (recordId: string) => {
      set({ isLoading: true, error: null })
      try {
        const response = await sapReservationsService.getSapReservationByRecordId(recordId)
        set({ currentReservation: response.data })
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "An unknown error occurred"
        set({ error: errorMessage })
        throw error
      } finally {
        set({ isLoading: false })
      }
    },

    createSapReservation: async (dto: ISapReservationDto) => {
      set({ isLoading: true, error: null })
      try {
        const response = await sapReservationsService.createSapReservation(dto)
        return response.data
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "An unknown error occurred"
        set({ error: errorMessage })
        throw error
      } finally {
        set({ isLoading: false })
      }
    },
  }))
)
